/**
 * Model-assisted occupation resolution (ADR-025 rung 3).
 *
 * Local heuristics have a ceiling and we reached it: a 22-year technology
 * delivery leader titled "Associate Director" could not be resolved from token
 * overlap, because the signal is semantic, not lexical.
 *
 * Two design constraints:
 *
 * 1. THE MODEL PICKS FROM OUR CANDIDATES. It receives a shortlist of real O*NET
 *    codes and returns one of them. It cannot invent an occupation, so a
 *    hallucination becomes a validation failure rather than a plausible-looking
 *    wrong answer flowing into every downstream screen.
 *
 * 2. NOTHING IDENTIFYING IS SENT. Titles, skills and role descriptions only —
 *    redacted client-side before the request is built (ADR-024). No name, no
 *    employer, no dates.
 */
import { callClaude } from "./api";
import { redactText } from "./redact";

export async function resolveWithModel(parsed, candidates, extraContext = "") {
  const shortlist = candidates.slice(0, 8).map((c) => `${c.code} — ${c.title}`).join("\n");

  const evidence = redactText(
    [
      `Roles held (most recent first): ${(parsed.roles || []).map((r) => r.title).join(" | ")}`,
      `Skills: ${(parsed.skills || []).slice(0, 12).join(", ")}`,
      `What they describe doing: ${(parsed.taskText || "").slice(0, 900)}`,
      extraContext ? `They added: ${extraContext}` : "",
    ].filter(Boolean).join("\n"),
    {}
  ).slice(0, 1800);

  const prompt = `You are matching a professional to a US O*NET occupation.

CANDIDATES (choose exactly one code from this list):
${shortlist}

EVIDENCE:
${evidence}

Pick the candidate that best matches what this person actually does. Job titles
often describe rank rather than occupation — weight the described work and role
history above the title. Return JSON only:
{"code":"<one code from the list>","confidence":0.0-1.0,"why":"<one short sentence citing the evidence>"}`;

  const result = await callClaude(prompt);

  // Validate against our own list — never trust a returned code blindly.
  const chosen = candidates.find((c) => c.code === result?.code);
  if (!chosen) throw new Error("Model returned a code outside the candidate list");

  return {
    code: chosen.code,
    title: chosen.title,
    confidence: Math.max(0, Math.min(1, Number(result.confidence) || 0.8)),
    why: String(result.why || "").slice(0, 200),
    via: "model",
  };
}
